import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Save, Euro } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPricing = () => {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState({
    basePrice: 8.0,
    pricePerKm: 1.50,
    pricePerMinute: 0.40,
    corporatePerHour: 60.0,
    hourlyRate: 75.0,
    hourlyMinimum: 3
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const adminUser = localStorage.getItem('adminUser');
    if (!adminUser) {
      navigate('/admin/login');
      return;
    }
    
    fetchPricing();
  }, [navigate]);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/admin/pricing`);
      setPricing(response.data);
    } catch (error) {
      console.error('Error fetching pricing:', error);
      toast.error('Erreur lors du chargement des tarifs');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/admin/pricing`, pricing);
      toast.success('Tarifs mis à jour avec succès!');
    } catch (error) {
      console.error('Error saving pricing:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setPricing({
      ...pricing,
      [field]: parseFloat(value) || 0
    });
  };

  const calculateExample = () => {
    const distance = 15;
    const duration = 25;
    return pricing.basePrice + (distance * pricing.pricePerKm) + (duration * pricing.pricePerMinute);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-gold text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
      <header className="bg-black/80 backdrop-blur-md border-b border-gold/20 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="outline"
              className="border-gold/30 text-gold hover:bg-gold hover:text-black"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au Dashboard
            </Button>
            <h1 className="text-2xl font-bold text-gold">Gestion des Tarifs</h1>
            <div className="w-32"></div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        <Card className="max-w-2xl mx-auto bg-gradient-to-br from-gray-900 to-black border-2 border-gold/30">
          <CardHeader>
            <CardTitle className="text-3xl text-white flex items-center gap-3">
              <Euro className="w-8 h-8 text-gold" />
              Configuration des Tarifs
            </CardTitle>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSave} className="space-y-8">
              {/* Base Price */}
              <div className="space-y-2">
                <Label htmlFor="basePrice" className="text-white text-lg">
                  Prix de Base
                </Label>
                <div className="relative">
                  <Input
                    id="basePrice"
                    type="number"
                    step="0.01"
                    min="0"
                    value={pricing.basePrice}
                    onChange={(e) => handleChange('basePrice', e.target.value)}
                    className="bg-gray-800 border-gold/30 text-white text-xl py-6 pr-12"
                    required
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gold text-xl">€</span>
                </div>
                <p className="text-sm text-gray-400">Montant minimum facturé à chaque course</p>
              </div>

              {/* Price per KM */}
              <div className="space-y-2">
                <Label htmlFor="pricePerKm" className="text-white text-lg">
                  Prix par Kilomètre
                </Label>
                <div className="relative">
                  <Input
                    id="pricePerKm"
                    type="number"
                    step="0.01"
                    min="0"
                    value={pricing.pricePerKm}
                    onChange={(e) => handleChange('pricePerKm', e.target.value)}
                    className="bg-gray-800 border-gold/30 text-white text-xl py-6 pr-16"
                    required
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gold text-xl">€/km</span>
                </div>
                <p className="text-sm text-gray-400">Coût par kilomètre parcouru</p>
              </div>

              {/* Price per Minute */}
              <div className="space-y-2">
                <Label htmlFor="pricePerMinute" className="text-white text-lg">
                  Prix par Minute
                </Label>
                <div className="relative">
                  <Input
                    id="pricePerMinute"
                    type="number"
                    step="0.01"
                    min="0"
                    value={pricing.pricePerMinute}
                    onChange={(e) => handleChange('pricePerMinute', e.target.value)}
                    className="bg-gray-800 border-gold/30 text-white text-xl py-6 pr-20"
                    required
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gold text-xl">€/min</span>
                </div>
                <p className="text-sm text-gray-400">Coût par minute de trajet (inclut trafic)</p>
              </div>

              {/* Divider */}
              <div className="border-t border-gold/30 pt-8">
                <h3 className="text-white text-xl font-semibold mb-6">📅 Réservations par Heure & Événements</h3>
              </div>

              {/* Hourly Rate */}
              <div className="space-y-2">
                <Label htmlFor="hourlyRate" className="text-white text-lg">
                  Tarif Horaire (Location par Heure)
                </Label>
                <div className="relative">
                  <Input
                    id="hourlyRate"
                    type="number"
                    step="0.01"
                    min="0"
                    value={pricing.hourlyRate || 75}
                    onChange={(e) => handleChange('hourlyRate', e.target.value)}
                    className="bg-gray-800 border-gold/30 text-white text-xl py-6 pr-16"
                    required
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gold text-xl">€/h</span>
                </div>
                <p className="text-sm text-gray-400">Prix par heure pour les locations horaires</p>
              </div>

              {/* Hourly Minimum */}
              <div className="space-y-2">
                <Label htmlFor="hourlyMinimum" className="text-white text-lg">
                  Minimum d'Heures (Location Horaire)
                </Label>
                <div className="relative">
                  <Input
                    id="hourlyMinimum"
                    type="number"
                    step="1"
                    min="1"
                    value={pricing.hourlyMinimum || 3}
                    onChange={(e) => handleChange('hourlyMinimum', e.target.value)}
                    className="bg-gray-800 border-gold/30 text-white text-xl py-6 pr-20"
                    required
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gold text-xl">heures</span>
                </div>
                <p className="text-sm text-gray-400">Nombre minimum d'heures à facturer</p>
              </div>

              {/* Corporate Rate */}
              <div className="space-y-2">
                <Label htmlFor="corporatePerHour" className="text-white text-lg">
                  Tarif Événement/Corporate (par Heure)
                </Label>
                <div className="relative">
                  <Input
                    id="corporatePerHour"
                    type="number"
                    step="0.01"
                    min="0"
                    value={pricing.corporatePerHour || 60}
                    onChange={(e) => handleChange('corporatePerHour', e.target.value)}
                    className="bg-gray-800 border-gold/30 text-white text-xl py-6 pr-16"
                    required
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gold text-xl">€/h</span>
                </div>
                <p className="text-sm text-gray-400">Prix par heure pour les événements professionnels</p>
              </div>

              {/* Example Calculation */}
              <div className="bg-gradient-to-r from-gold/20 to-amber-600/20 border-2 border-gold/50 rounded-lg p-6">
                <h3 className="text-white text-lg font-semibold mb-3">Exemple de Calcul</h3>
                <div className="space-y-2 text-gray-300">
                  <p>Bruxelles Centre → Aéroport (15 km, 25 min)</p>
                  <div className="flex items-center justify-between text-sm">
                    <span>Prix de base:</span>
                    <span className="text-white">{pricing.basePrice.toFixed(2)}€</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>Distance (15 km × {pricing.pricePerKm}€):</span>
                    <span className="text-white">{(15 * pricing.pricePerKm).toFixed(2)}€</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>Durée (25 min × {pricing.pricePerMinute}€):</span>
                    <span className="text-white">{(25 * pricing.pricePerMinute).toFixed(2)}€</span>
                  </div>
                  <div className="border-t border-gold/30 pt-2 mt-2 flex items-center justify-between">
                    <span className="font-bold text-lg">TOTAL:</span>
                    <span className="text-gold font-bold text-2xl">{calculateExample().toFixed(2)}€</span>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <Button
                type="submit"
                size="lg"
                disabled={saving}
                className="w-full bg-gradient-to-r from-gold to-amber-600 text-black hover:from-amber-600 hover:to-gold text-lg py-6"
              >
                <Save className="w-5 h-5 mr-2" />
                {saving ? 'Sauvegarde...' : 'Sauvegarder les Tarifs'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPricing;
